import type {
    SketchDimensionOrientation,
    SketchDimensionReferenceRecord,
} from '../SketchDimensionTypes';
import {SketchDimensionPlacementResolver} from './SketchDimensionPlacementResolver';
import type {
    SketchDimensionInferenceRegistry,
    SmartDimensionInferenceInput,
} from './SketchDimensionInferenceRegistry';
import type {
    SmartDimensionCandidate,
    SmartDimensionPickItem,
    SmartDimensionVec2,
} from './SketchSmartDimensionTypes';

/**
 * 智能尺寸内置推断策略（阶段 4：SD-SEL-001~011 已确认项）。
 *
 * 文件职责：
 * 1. 单线长度、两点距离（方向切换）、点线垂距、平行线距/两线角、三点角、
 *    圆直径、圆弧半径、圆系-实体距离（默认测至圆心）、两圆中心距；
 * 2. 全部输出纯数据候选：几何量核心单位米/弧度，引用为持久 ID 复合身份；
 * 3. 中心线/象限点/真弧长/Shift 附着切换等高级语义属阶段 6，按新签名注册，
 *    不修改本文件策略。
 */

/** 几何退化判定阈值（米）。 */
const DEGENERATE_LENGTH = 1e-12;

/** 读取点类选择项的位置（点自身或圆系圆心）。 */
function readPickAnchor(pick: SmartDimensionPickItem): SmartDimensionVec2 | null {
    if (pick.itemKind === 'point') {
        return pick.point ?? null;
    }
    if (pick.itemKind === 'circle' || pick.itemKind === 'arc') {
        return pick.center ?? null;
    }

    return null;
}

/** 构造点类选择项的稳定引用（圆系默认附着圆心；Shift 选择切换近侧圆周 Min，SD-SEL-011）。 */
function toAnchorReference(pick: SmartDimensionPickItem, selectionOrder: number): SketchDimensionReferenceRecord {
    if (pick.itemKind === 'circle' || pick.itemKind === 'arc') {
        return {
            entityId: pick.entityId,
            topologyRole: 'center',
            ...(pick.centerPointId ? {pointId: pick.centerPointId} : {}),
            selectionOrder,
            /**
             * 01 基线 §6.2：Shift 参与选择的圆系引用附着到圆周（近侧 = Min）；
             * 顺序反转/混合组合的精确矩阵为 V 级待实机。
             */
            arcCondition: pick.shiftKey ? 'min' : 'center',
        };
    }

    return {
        entityId: pick.entityId,
        topologyRole: pick.topologyRole ?? 'point',
        ...(pick.pointId ? {pointId: pick.pointId} : {}),
        selectionOrder,
    };
}

/** 构造实体引用。 */
function toEntityReference(pick: SmartDimensionPickItem, selectionOrder: number): SketchDimensionReferenceRecord {
    return {
        entityId: pick.entityId,
        topologyRole: 'entity',
        selectionOrder,
    };
}

/** 读取线选择项的单位方向；零长度返回 null。 */
function readLineDirection(pick: SmartDimensionPickItem): SmartDimensionVec2 | null {
    if (!pick.lineStart || !pick.lineEnd) {
        return null;
    }

    const dx = pick.lineEnd[0] - pick.lineStart[0];
    const dy = pick.lineEnd[1] - pick.lineStart[1];
    const length = Math.hypot(dx, dy);

    return length < DEGENERATE_LENGTH ? null : [dx / length, dy / length];
}

/** 点到无限直线的有符号垂距（方向×点向量叉积）。 */
function signedPointLineDistance(
    point: SmartDimensionVec2,
    lineStart: SmartDimensionVec2,
    direction: SmartDimensionVec2,
): number {
    return direction[0] * (point[1] - lineStart[1]) - direction[1] * (point[0] - lineStart[0]);
}

/**
 * 构造两锚点距离候选（两点、两圆心、圆心-点共用；SD-SEL-002/010/011）。
 */
function createAnchorDistanceCandidate(
    firstPick: SmartDimensionPickItem,
    secondPick: SmartDimensionPickItem,
    pointer: SmartDimensionVec2,
): SmartDimensionCandidate | null {
    const first = readPickAnchor(firstPick);
    const second = readPickAnchor(secondPick);

    if (!first || !second) {
        return null;
    }

    /** Shift 切换到近侧圆周的圆系引用产生 -r 偏置（测量值 = 中心距 - Σr）。 */
    const shiftRadiusOffset = [firstPick, secondPick].reduce((sum, pick) => (
        (pick.itemKind === 'circle' || pick.itemKind === 'arc') && pick.shiftKey && pick.radius
            ? sum - pick.radius
            : sum
    ), 0);
    let orientation = SketchDimensionPlacementResolver.resolveTwoPointOrientation(first, second, pointer);

    // Min/Max 附着与水平/竖直单轴距离的组合矩阵为 V 级待实机：附着偏置存在时按线性距离处理。
    if (shiftRadiusOffset !== 0) {
        orientation = 'aligned';
    }

    const valueByOrientation: Record<SketchDimensionOrientation, number> = {
        aligned: Math.hypot(second[0] - first[0], second[1] - first[1]) + shiftRadiusOffset,
        horizontal: Math.abs(second[0] - first[0]),
        vertical: Math.abs(second[1] - first[1]),
    };
    const currentValue = valueByOrientation[orientation];

    if (currentValue < DEGENERATE_LENGTH) {
        return null;
    }

    const directionSign: 1 | -1 = orientation === 'horizontal'
        ? (second[0] - first[0] >= 0 ? 1 : -1)
        : (second[1] - first[1] >= 0 ? 1 : -1);

    return {
        dimensionKind: 'point-distance',
        orientation,
        currentValue,
        valueScale: 1,
        references: [toAnchorReference(firstPick, 0), toAnchorReference(secondPick, 1)],
        branch: {
            orientation,
            ...(orientation === 'aligned' ? {} : {directionSign}),
        },
        previewAnchors: [first, second],
    };
}

/**
 * 中心线专用候选（SD-SEL-014/015/016）。
 *
 * @param measuredPick 被测方（点或普通线）。
 * @param centerlinePick 中心线。
 * @param pointer 指针位置。
 * @returns 候选；几何退化返回 null。
 */
function createCenterlineCandidate(
    measuredPick: SmartDimensionPickItem,
    centerlinePick: SmartDimensionPickItem,
    pointer: SmartDimensionVec2,
): SmartDimensionCandidate | null {
    const centerlineDirection = readLineDirection(centerlinePick);

    if (!centerlineDirection || !centerlinePick.lineStart) {
        return null;
    }

    const measuredAnchor = measuredPick.itemKind === 'point'
        ? measuredPick.point ?? null
        : measuredPick.lineStart ?? null;

    if (!measuredAnchor) {
        return null;
    }

    //------ 对称角（非平行线 + 中心线）开始------
    if (measuredPick.itemKind === 'line') {
        const measuredDirection = readLineDirection(measuredPick);

        if (measuredDirection
            && !SketchDimensionPlacementResolver.areDirectionsParallel(measuredDirection, centerlineDirection)) {
            const intersection = SketchDimensionPlacementResolver.intersectLines(
                measuredPick.lineStart!, measuredPick.lineEnd!,
                centerlinePick.lineStart, centerlinePick.lineEnd!,
            );
            const angleZone = intersection
                ? SketchDimensionPlacementResolver.resolveAngleZone(
                    measuredDirection, centerlineDirection, intersection, pointer,
                )
                : null;

            if (!angleZone) {
                return null;
            }

            /** 默认半角（SD-SEL-016）；放置时 Shift 由控制器升级为完整对称角。 */
            return {
                dimensionKind: 'symmetric-angle',
                currentValue: angleZone.targetAngle,
                valueScale: 1,
                references: [toEntityReference(measuredPick, 0), toEntityReference(centerlinePick, 1)],
                branch: {
                    angleQuadrant: angleZone.quadrant,
                    directionSign: angleZone.branchSign,
                    symmetricAngleMode: 'half',
                },
                // 对称角与普通两线角共用布局协议，必须保留被测线与中心线的完整端点。
                previewAnchors: [
                    measuredPick.lineStart!, measuredPick.lineEnd!,
                    centerlinePick.lineStart, centerlinePick.lineEnd!,
                ],
            };
        }
    }
    //------ 对称角（非平行线 + 中心线）结束------

    const signedMeasured = signedPointLineDistance(measuredAnchor, centerlinePick.lineStart, centerlineDirection);
    const signedPointer = signedPointLineDistance(pointer, centerlinePick.lineStart, centerlineDirection);

    if (Math.abs(signedMeasured) < DEGENERATE_LENGTH) {
        return null;
    }

    /**
     * 近/远侧语义（SD-SEL-014/015）：指针与被测方同侧 = 单倍距离（半径式），
     * 指针跨过中心线到远侧 = 双倍距离（直径式，旋转轮廓直径语义）。
     */
    const farSide = Math.sign(signedPointer) !== 0 && Math.sign(signedPointer) !== Math.sign(signedMeasured);
    const measuredReference = measuredPick.itemKind === 'point'
        ? toAnchorReference(measuredPick, 0)
        : toEntityReference(measuredPick, 0);
    const references = [measuredReference, toEntityReference(centerlinePick, 1)];
    const previewAnchors: SmartDimensionVec2[] = [
        measuredAnchor,
        centerlinePick.lineStart,
        centerlinePick.lineEnd!,
    ];
    const branchSign: 1 | -1 = signedMeasured >= 0 ? 1 : -1;

    if (farSide) {
        return {
            dimensionKind: 'doubled-distance',
            currentValue: Math.abs(signedMeasured) * 2,
            valueScale: 2,
            references,
            branch: {directionSign: branchSign, centerlineSide: 'far'},
            previewAnchors,
        };
    }

    return {
        dimensionKind: measuredPick.itemKind === 'point' ? 'point-line-distance' : 'line-line-distance',
        currentValue: Math.abs(signedMeasured),
        valueScale: 1,
        references,
        branch: {directionSign: branchSign, centerlineSide: 'near'},
        previewAnchors,
    };
}

/**
 * 真弧长候选（SD-SEL-008：弧 + Ctrl 补选两端点）。
 */
function createArcLengthCandidate(
    arcPick: SmartDimensionPickItem,
    endpointPicks: readonly SmartDimensionPickItem[],
): SmartDimensionCandidate | null {
    const center = arcPick.center;

    if (!center || !arcPick.centerPointId || endpointPicks.length !== 2) {
        return null;
    }
    // 端点必须属于该弧（宿主实体一致），否则组合无真弧长语义。
    if (!endpointPicks.every((pick) => pick.entityId === arcPick.entityId && pick.point && pick.pointId)) {
        return null;
    }

    const start = endpointPicks[0]!.point!;
    const end = endpointPicks[1]!.point!;
    const startRadius = Math.hypot(start[0] - center[0], start[1] - center[1]);
    const endRadius = Math.hypot(end[0] - center[0], end[1] - center[1]);

    if (startRadius < DEGENERATE_LENGTH || endRadius < DEGENERATE_LENGTH) {
        return null;
    }

    const branchSign: 1 | -1 = arcPick.clockwise ? -1 : 1;
    let sweep = branchSign === 1
        ? Math.atan2(end[1] - center[1], end[0] - center[0]) - Math.atan2(start[1] - center[1], start[0] - center[0])
        : Math.atan2(start[1] - center[1], start[0] - center[0]) - Math.atan2(end[1] - center[1], end[0] - center[0]);

    sweep %= Math.PI * 2;
    if (sweep <= 0) {
        sweep += Math.PI * 2;
    }

    return {
        dimensionKind: 'arc-length',
        currentValue: ((startRadius + endRadius) / 2) * sweep,
        valueScale: 1,
        references: [
            toEntityReference(arcPick, 0),
            {
                entityId: arcPick.entityId,
                topologyRole: 'center',
                pointId: arcPick.centerPointId,
                selectionOrder: 1,
            },
            {...toAnchorReference(endpointPicks[0]!, 2)},
            {...toAnchorReference(endpointPicks[1]!, 3)},
        ],
        branch: {directionSign: branchSign},
        previewAnchors: [start, end],
    };
}

/**
 * 单线长度策略（SD-SEL-001）。
 *
 * 对齐 SOLIDWORKS：单线尺寸随指针在平行（真长）/水平/竖直三种投影间切换
 * （斜线尤其明显）；右键锁定分支。方向语义复用两端点的 orientation 判定，
 * `length` + `branch.orientation` 的方程/测量/布局已在阶段 8 H/V 命令统一支持。
 */
function inferLineLength(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const linePick = input.picks[0]!;
    const start = linePick.lineStart;
    const end = linePick.lineEnd;

    if (!start || !end) {
        return null;
    }

    const alignedLength = Math.hypot(end[0] - start[0], end[1] - start[1]);

    if (alignedLength < DEGENERATE_LENGTH) {
        return null;
    }

    let orientation = SketchDimensionPlacementResolver.resolveTwoPointOrientation(start, end, input.pointer);
    let currentValue = orientation === 'horizontal'
        ? Math.abs(end[0] - start[0])
        : orientation === 'vertical'
            ? Math.abs(end[1] - start[1])
            : alignedLength;

    // 投影退化（水平线的竖直投影、无指针时的默认指针落在端点上等）回退到平行真长。
    if (currentValue < DEGENERATE_LENGTH) {
        orientation = 'aligned';
        currentValue = alignedLength;
    }

    return {
        dimensionKind: 'length',
        orientation,
        currentValue,
        valueScale: 1,
        references: [toEntityReference(linePick, 0)],
        branch: orientation === 'aligned' ? {} : {orientation},
        previewAnchors: [start, end],
    };
}

/** 两点距离策略（SD-SEL-002：水平/竖直/线性随指针切换）。 */
function inferPointPointDistance(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    return createAnchorDistanceCandidate(input.picks[0]!, input.picks[1]!, input.pointer);
}

/** 点线垂距策略（SD-SEL-003；中心线走近/远侧半径-直径式路由 SD-SEL-015）。 */
function inferPointLineDistance(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const pointPick = input.picks.find((pick) => pick.itemKind === 'point');
    const linePick = input.picks.find((pick) => pick.itemKind === 'line');

    if (!pointPick || !linePick || !pointPick.point || !linePick.lineStart) {
        return null;
    }

    //------ 中心线半径/直径式距离 开始------
    if (linePick.isCenterline) {
        return createCenterlineCandidate(pointPick, linePick, input.pointer);
    }
    //------ 中心线半径/直径式距离 结束------

    const direction = readLineDirection(linePick);

    if (!direction) {
        return null;
    }

    const signedDistance = signedPointLineDistance(pointPick.point, linePick.lineStart, direction);

    if (Math.abs(signedDistance) < DEGENERATE_LENGTH) {
        return null;
    }

    return {
        dimensionKind: 'point-line-distance',
        currentValue: Math.abs(signedDistance),
        valueScale: 1,
        references: input.picks.map((pick, index) => (
            pick.itemKind === 'point' ? toAnchorReference(pick, index) : toEntityReference(pick, index)
        )),
        branch: {directionSign: signedDistance >= 0 ? 1 : -1},
        previewAnchors: [pointPick.point, linePick.lineStart, linePick.lineEnd!],
    };
}

/** 两线策略（SD-SEL-004 平行线距 / SD-SEL-005 两线角 / 中心线双倍距离与对称角路由）。 */
function inferLineLine(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const firstPick = input.picks[0]!;
    const secondPick = input.picks[1]!;

    //------ 中心线语义路由 开始------
    // 恰有一条中心线：走中心线专用候选（近侧单倍/远侧双倍/非平行对称角）。
    if (Boolean(firstPick.isCenterline) !== Boolean(secondPick.isCenterline)) {
        const centerlinePick = firstPick.isCenterline ? firstPick : secondPick;
        const measuredPick = firstPick.isCenterline ? secondPick : firstPick;

        return createCenterlineCandidate(measuredPick, centerlinePick, input.pointer);
    }
    //------ 中心线语义路由 结束------

    const firstDirection = readLineDirection(firstPick);
    const secondDirection = readLineDirection(secondPick);

    if (!firstDirection || !secondDirection || !firstPick.lineStart || !secondPick.lineStart) {
        return null;
    }

    if (SketchDimensionPlacementResolver.areDirectionsParallel(firstDirection, secondDirection)) {
        // 平行线垂距：第二线起点到第一线的距离（编译器同构语义）。
        const signedDistance = signedPointLineDistance(secondPick.lineStart, firstPick.lineStart, firstDirection);

        if (Math.abs(signedDistance) < DEGENERATE_LENGTH) {
            return null;
        }

        return {
            dimensionKind: 'line-line-distance',
            currentValue: Math.abs(signedDistance),
            valueScale: 1,
            references: [toEntityReference(firstPick, 0), toEntityReference(secondPick, 1)],
            branch: {directionSign: signedDistance >= 0 ? 1 : -1},
            previewAnchors: [firstPick.lineStart, firstPick.lineEnd!, secondPick.lineStart, secondPick.lineEnd!],
        };
    }

    const intersection = SketchDimensionPlacementResolver.intersectLines(
        firstPick.lineStart, firstPick.lineEnd!,
        secondPick.lineStart, secondPick.lineEnd!,
    );

    if (!intersection) {
        return null;
    }

    const angleZone = SketchDimensionPlacementResolver.resolveAngleZone(
        firstDirection, secondDirection, intersection, input.pointer,
    );

    if (!angleZone) {
        return null;
    }

    return {
        dimensionKind: 'angle',
        currentValue: angleZone.targetAngle,
        valueScale: 1,
        references: [toEntityReference(firstPick, 0), toEntityReference(secondPick, 1)],
        branch: {angleQuadrant: angleZone.quadrant, directionSign: angleZone.branchSign},
        // 角度预览与确认态共用完整布局契约，必须传入两条真实直线的四个端点。
        previewAnchors: [firstPick.lineStart, firstPick.lineEnd!, secondPick.lineStart, secondPick.lineEnd!],
    };
}

/** 三点角策略（SD-SEL-006：第一点为角顶点）。 */
function inferThreePointAngle(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const [vertexPick, firstRayPick, secondRayPick] = input.picks;
    const vertex = vertexPick?.point;
    const firstRay = firstRayPick?.point;
    const secondRay = secondRayPick?.point;

    if (!vertex || !firstRay || !secondRay) {
        return null;
    }

    const firstLength = Math.hypot(firstRay[0] - vertex[0], firstRay[1] - vertex[1]);
    const secondLength = Math.hypot(secondRay[0] - vertex[0], secondRay[1] - vertex[1]);

    if (firstLength < DEGENERATE_LENGTH || secondLength < DEGENERATE_LENGTH) {
        return null;
    }

    const firstDirection: SmartDimensionVec2 = [
        (firstRay[0] - vertex[0]) / firstLength,
        (firstRay[1] - vertex[1]) / firstLength,
    ];
    const secondDirection: SmartDimensionVec2 = [
        (secondRay[0] - vertex[0]) / secondLength,
        (secondRay[1] - vertex[1]) / secondLength,
    ];
    const angleZone = SketchDimensionPlacementResolver.resolveAngleZone(
        firstDirection, secondDirection, vertex, input.pointer,
    );

    if (!angleZone) {
        return null;
    }

    return {
        dimensionKind: 'three-point-angle',
        currentValue: angleZone.targetAngle,
        valueScale: 1,
        references: input.picks.map((pick, index) => toAnchorReference(pick!, index)),
        branch: {angleQuadrant: angleZone.quadrant, directionSign: angleZone.branchSign},
        previewAnchors: [vertex, firstRay, secondRay],
    };
}

/** 单圆直径策略（SD-SEL-009）。 */
function inferCircleDiameter(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const circlePick = input.picks[0]!;

    if (!circlePick.center || !circlePick.radius || circlePick.radius < DEGENERATE_LENGTH) {
        return null;
    }

    return {
        dimensionKind: 'diameter',
        currentValue: circlePick.radius * 2,
        valueScale: 2,
        references: [toEntityReference(circlePick, 0)],
        branch: {},
        previewAnchors: [circlePick.center],
    };
}

/** 单圆弧半径策略（SD-SEL-007）。 */
function inferArcRadius(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const arcPick = input.picks[0]!;

    if (!arcPick.center || !arcPick.radius || arcPick.radius < DEGENERATE_LENGTH) {
        return null;
    }

    return {
        dimensionKind: 'radius',
        currentValue: arcPick.radius,
        valueScale: 1,
        references: [toEntityReference(arcPick, 0)],
        branch: {},
        previewAnchors: [arcPick.center],
    };
}

/**
 * 样条总长策略。
 *
 * 对齐 SOLIDWORKS：给样条标注尺寸得到的是**总长**（关系表现为`距离`）。
 * 当前值取采样折线弧长——与渲染、测量共用同一条曲线，口径一致。
 *
 * @param input 推断输入。
 * @returns 候选；采样退化返回 null。
 */
function inferSplineLength(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const splinePick = input.picks[0]!;
    const curve = splinePick.splineCurve;
    const length = splinePick.splineLength;

    if (!curve || curve.length < 2 || !length || length < DEGENERATE_LENGTH) {
        return null;
    }

    return {
        dimensionKind: 'spline-length',
        currentValue: length,
        valueScale: 1,
        references: [toEntityReference(splinePick, 0)],
        branch: {},
        previewAnchors: [curve[Math.floor(curve.length / 2)]!],
    };
}

/** 圆系与线的圆心垂距策略（SD-SEL-010：默认测至圆心）。 */
function inferCircleLineDistance(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const circlePick = input.picks.find((pick) => pick.itemKind === 'circle' || pick.itemKind === 'arc');
    const linePick = input.picks.find((pick) => pick.itemKind === 'line');

    if (!circlePick || !linePick || !circlePick.center || !linePick.lineStart) {
        return null;
    }

    const direction = readLineDirection(linePick);

    if (!direction) {
        return null;
    }

    const signedDistance = signedPointLineDistance(circlePick.center, linePick.lineStart, direction);

    if (Math.abs(signedDistance) < DEGENERATE_LENGTH) {
        return null;
    }

    return {
        dimensionKind: 'point-line-distance',
        currentValue: Math.abs(signedDistance),
        valueScale: 1,
        references: input.picks.map((pick, index) => (
            pick.itemKind === 'line' ? toEntityReference(pick, index) : toAnchorReference(pick, index)
        )),
        branch: {directionSign: signedDistance >= 0 ? 1 : -1},
        previewAnchors: [circlePick.center, linePick.lineStart, linePick.lineEnd!],
    };
}

/** 双锚点（圆系/点混合）距离策略包装。 */
function inferAnchorDistance(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    return createAnchorDistanceCandidate(input.picks[0]!, input.picks[1]!, input.pointer);
}

/** 真弧长策略包装（SD-SEL-008：弧 + Ctrl 补选两端点）。 */
function inferArcLength(input: SmartDimensionInferenceInput): SmartDimensionCandidate | null {
    const arcPick = input.picks.find((pick) => pick.itemKind === 'arc');
    const endpointPicks = input.picks.filter((pick) => pick.itemKind === 'point');

    return arcPick ? createArcLengthCandidate(arcPick, endpointPicks) : null;
}

/**
 * 注册全部内置策略。
 *
 * @param registry 推断策略注册表。
 */
export function registerBuiltinSmartDimensionInferenceStrategies(
    registry: SketchDimensionInferenceRegistry,
): void {
    registry.register('line', inferLineLength);
    registry.register('point+point', inferPointPointDistance);
    registry.register('line+point', inferPointLineDistance);
    registry.register('line+line', inferLineLine);
    registry.register('point+point+point', inferThreePointAngle);
    registry.register('circle', inferCircleDiameter);
    registry.register('arc', inferArcRadius);
    registry.register('spline', inferSplineLength);
    registry.register('circle+circle', inferAnchorDistance);
    registry.register('arc+arc', inferAnchorDistance);
    registry.register('arc+circle', inferAnchorDistance);
    registry.register('circle+point', inferAnchorDistance);
    registry.register('arc+point', inferAnchorDistance);
    registry.register('circle+line', inferCircleLineDistance);
    registry.register('arc+line', inferCircleLineDistance);
    registry.register('arc+point+point', inferArcLength);
}
